import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';

const Header = React.lazy(() => import('../components/header'));
const Footer = React.lazy(() => import('../components/Footer'));
import AppRouter from './AppRouter';


// Lazy load modals (important)
const Register = lazy(() => import('../components/register'));
const Login = lazy(() => import('../components/Login'));
const AdminLogin = lazy(() => import('../components/AdminLogin'));
const Confirmation = lazy(() => import('../components/Confirmation'));
const MobileAppPrompt = lazy(() => import('../components/MobileAppPrompt'));

import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { notificationService } from '../services/notificationService';
import { initTranslationHelpers } from '../utils/translationHelpers';

import '../styles/styles.css';
import '../styles/Modal.css';
import '../styles/animations.css';
// ❌ removed AdminDashboard.css (move it into AdminDashboard instead)

function AppShell() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  const navigate = useNavigate();

  useEffect(() => {

      const handleSWMessage = (event) => {

          if (event.data?.type === 'FCM_NOTIFICATION_CLICK') {

              const url = event.data.url;

              if (url) {
                  navigate(url);
              }
          }
      };

      navigator.serviceWorker.addEventListener('message', handleSWMessage);

      return () => {
          navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      };

  }, [navigate]);

  useEffect(() => {
    // Delay non-critical helpers
    const translationTimer = setTimeout(() => {
      initTranslationHelpers();
    }, 2000);

    // SW update prompt
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.ready.then(reg => {
            reg.addEventListener('updatefound', () => {
                const newSW = reg.installing;
                newSW.addEventListener('statechange', () => {
                    if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
                        if (window.confirm('A new version of eFilmHub is available. Refresh now?')) {
                            window.location.reload();
                        }
                    }
                });
            });
        });
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setIsAuthenticated(!!user);

      if (user) {
        try {
          // Delay notifications (not needed immediately)
          setTimeout(async () => {
            await notificationService.initialize();

            const permission = await notificationService.requestPermission();

            //if (permission === 'granted') {
              //await notificationService.subscribeUser();
              //notificationService.subscribeToNotifications(() => {
                //notificationService.updateBadge();
              //});
            //}
          }, 3000);
        } catch (error) {
          console.error('Notification error:', error);
        }
      }
    });

    return () => {
      clearTimeout(translationTimer);
      unsubscribe();
    };
  }, []);

  const closeModals = () => {
    setShowRegisterModal(false);
    setShowLoginModal(false);
    setShowAdminModal(false);
    setShowConfirmation(false);
  };

  const onRegistrationSuccess = () => {
    setShowRegisterModal(false);
    setConfirmationMessage('Registration successful! Welcome to eFilmHub.');
    setShowConfirmation(true);
  };

  const onLoginSuccess = () => {
    setShowLoginModal(false);
  };

  return (
    <div className="app">
      <Header
        isAuthenticated={isAuthenticated}
        setIsAuthenticated={setIsAuthenticated}
        setShowRegisterModal={setShowRegisterModal}
        setShowLoginModal={setShowLoginModal}
        setShowAdminModal={setShowAdminModal}
      />

      {/* Lazy-loaded modals */}
      <Suspense fallback={null}>
        {showRegisterModal && (
          <Register onRegistrationSuccess={onRegistrationSuccess} onClose={closeModals} />
        )}

        {showLoginModal && (
          <Login onLoginSuccess={onLoginSuccess} onClose={closeModals} />
        )}

        {showAdminModal && (
          <AdminLogin onAdminLogin={onLoginSuccess} onClose={closeModals} />
        )}

        {showConfirmation && (
          <Confirmation message={confirmationMessage} onClose={closeModals} />
        )}

        {/* Optional: lazy mobile prompt */}
        <MobileAppPrompt />
      </Suspense>

      <AppRouter isAuthenticated={isAuthenticated} />

      <Footer setShowAdminModal={setShowAdminModal} />
    </div>
  );
}

export default AppShell;